import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import platformAPI from "../../../services/platformApi";
import L from "../../../constants/baysportLabels";
import {
  formatRub,
  HealthRing,
  InsightList,
  MetricCard,
  PlanBucketCard,
  SubscriptionsTable,
} from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const PIE_COLORS = ["#0f766e", "#2563eb", "#ca8a04", "#64748b", "#dc2626"];

function formatWeekShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

function formatDayShort(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

export default function PlatformDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [payStats, setPayStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      platformAPI.getAnalytics(),
      platformAPI.getPaymentsStats().catch(() => null),
      platformAPI.listPayments({ limit: 8, status: "confirmed" }).catch(() => null),
    ])
      .then(([a, p, payments]) => {
        if (cancelled) return;
        setAnalytics(a.analytics || null);
        setPayStats(p?.stats || null);
        setRecentPayments(payments?.payments || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить сводку");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const overview = analytics?.overview || {};
  const growth = analytics?.growth || {};
  const activity = analytics?.activity || {};
  const subscriptions = analytics?.subscriptions || {};
  const forecast = analytics?.forecast || {};

  const signupSeries = useMemo(
    () =>
      (growth.signups_weekly || []).map((p) => ({
        name: formatWeekShort(p.week),
        value: p.value,
      })),
    [growth.signups_weekly],
  );

  const sessionSeries = useMemo(
    () =>
      (activity.sessions_weekly || []).map((p) => ({
        name: formatWeekShort(p.week),
        value: p.value,
      })),
    [activity.sessions_weekly],
  );

  const revenueSeries = useMemo(
    () =>
      (payStats?.revenue_daily || [])
        .filter((_, i, arr) => i >= arr.length - 14)
        .map((p) => ({
          name: formatDayShort(p.day),
          amount: p.amount,
        })),
    [payStats?.revenue_daily],
  );

  const planPie = useMemo(
    () =>
      (subscriptions.by_plan || [])
        .filter((p) => p.count > 0)
        .map((p) => ({ name: p.label, value: p.count, mrr: p.mrr_rub })),
    [subscriptions.by_plan],
  );

  return (
    <div className={s.page}>
      <PlatformPageHeader
        eyebrow={`${L.productName} · платформа`}
        title="Сводка"
        lead="MRR, оплаты клубов, рост и риски — всё для управления SaaS."
        actions={
          <>
            <Link to="/admin/payments" className={s.heroActionBtn}>
              Оплаты
            </Link>
            <Link to="/admin/plans" className={s.heroActionBtnGhost}>
              Тарифы
            </Link>
          </>
        }
      />

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка аналитики…</div> : null}

      {!loading && analytics ? (
        <>
          <section className={s.metricGrid}>
            <MetricCard
              label="MRR"
              value={`${formatRub(overview.mrr_estimate_rub)} ₽`}
              hint={`ARR ~ ${formatRub(overview.arr_estimate_rub)} ₽`}
              accent
              trend={forecast?.tenants?.trend}
            />
            <MetricCard
              label="Оплаты 30 дн."
              value={`${formatRub(payStats?.revenue_30d_rub)} ₽`}
              hint={`За 7 дн.: ${formatRub(payStats?.revenue_7d_rub)} ₽`}
            />
            <MetricCard
              label="Клубы"
              value={overview.tenants_total || 0}
              hint={`Активных: ${overview.tenants_by_status?.active || 0} · Пробных: ${overview.tenants_by_status?.trial || 0}`}
            />
            <MetricCard
              label="Новые за 30 дн."
              value={growth.new_tenants_30d || 0}
              hint={`За неделю: ${growth.new_tenants_7d || 0} · Рост ${growth.growth_rate_pct || 0}%`}
              trend={
                (growth.growth_rate_pct || 0) > 5
                  ? "up"
                  : (growth.growth_rate_pct || 0) < -5
                    ? "down"
                    : "flat"
              }
            />
            <article className={s.metricCard}>
              <div className={s.metricLabel}>Здоровье платформы</div>
              <div className={s.healthMetricRow}>
                <HealthRing score={overview.health_score || 0} />
                <div className={s.metricHint}>
                  Активных клубов: {activity.active_clubs_30d || 0}
                  <br />
                  Утилизация: {activity.utilization_pct || 0}%
                </div>
              </div>
            </article>
          </section>

          <section className={s.dashGrid}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Выручка SaaS (14 дн.)</h3>
                <Link to="/admin/payments" className={s.panelLink}>
                  Все оплаты
                </Link>
              </div>
              {revenueSeries.some((p) => p.amount > 0) ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={revenueSeries}>
                    <defs>
                      <linearGradient id="paRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0f766e" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={48} />
                    <Tooltip
                      formatter={(v) => [`${formatRub(v)} ₽`, "Выручка"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#0f766e"
                      fill="url(#paRev)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className={s.emptyHint}>Пока нет подтверждённых оплат</p>
              )}
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Регистрации клубов</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={signupSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                  <Tooltip />
                  <Bar dataKey="value" name="Клубы" fill="#2563eb" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Тренировки по неделям</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={sessionSeries}>
                  <defs>
                    <linearGradient id="paSes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    fill="url(#paSes)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Клубы по тарифам</h3>
              {planPie.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={planPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                    >
                      {planPie.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, _n, item) => [
                        `${v} клубов · MRR ${formatRub(item?.payload?.mrr)} ₽`,
                        item?.payload?.name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className={s.emptyHint}>Нет данных по тарифам</p>
              )}
              <div className={s.planBucketRow}>
                {(subscriptions.by_plan || []).map((p) => (
                  <PlanBucketCard
                    key={p.bucket}
                    bucket={p.bucket}
                    label={p.label}
                    count={p.count}
                    mrrRub={p.mrr_rub}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className={s.dashSplit}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Последние оплаты</h3>
                <Link to="/admin/payments" className={s.panelLink}>
                  Смотреть все
                </Link>
              </div>
              {recentPayments.length ? (
                <div className={s.payList}>
                  {recentPayments.map((p) => (
                    <Link
                      key={p.id}
                      to={p.tenant_id ? `/admin/tenants/${p.tenant_id}` : "/admin/payments"}
                      className={s.payRow}
                    >
                      <div className={s.payMeta}>
                        <strong>{p.tenant_name || p.tenant_subdomain || "Клуб"}</strong>
                        <small>
                          {p.plan_name || "Тариф"} ·{" "}
                          {p.confirmed_at
                            ? new Date(p.confirmed_at).toLocaleDateString("ru-RU")
                            : "—"}
                        </small>
                      </div>
                      <span className={s.payAmount}>
                        {formatRub(p.amount)} ₽
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className={s.emptyHint}>Подтверждённых оплат пока нет</p>
              )}
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Инсайты</h3>
              <InsightList items={analytics.insights || []} />
              <div className={s.atRiskRow}>
                <div className={s.atRiskItem}>
                  <strong>{analytics.at_risk?.expiring_soon || 0}</strong>
                  <span>Истекает скоро</span>
                </div>
                <div className={s.atRiskItem}>
                  <strong>{analytics.at_risk?.inactive_trials || 0}</strong>
                  <span>Неактивные trial</span>
                </div>
                <div className={s.atRiskItem}>
                  <strong>{analytics.at_risk?.suspended || 0}</strong>
                  <span>Отключены</span>
                </div>
              </div>
            </div>
          </section>

          <SubscriptionsTable
            title="Подписки — истекают в ближайшие 14 дней"
            rows={subscriptions.expiring_14d || []}
            emptyText="Нет клубов с истекающей подпиской"
          />

          <div className={s.quickLinks}>
            <Link to="/admin/tenants" className={s.quickLink}>
              Все клубы
            </Link>
            <Link to="/admin/support" className={s.quickLink}>
              Поддержка
            </Link>
            <Link to="/admin/plans" className={s.quickLink}>
              Управление тарифами
            </Link>
            <Link to="/admin/audit" className={s.quickLink}>
              Журнал аудита
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}
